<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { useAlert } from 'dashboard/composables';
import { useCommercialFeatures } from 'dashboard/composables/useCommercialFeatures';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CommercialQuotesAPI from 'dashboard/api/commercialQuotes';
import CommercialCatalogAPI from 'dashboard/api/commercialCatalog';

const props = defineProps({
  dealId: { type: [Number, String], required: true },
  defaultCurrency: { type: String, default: 'USD' },
});
const emit = defineEmits(['changed']);
const { t } = useI18n();
const { hasFeature } = useCommercialFeatures();
const quotes = ref([]);
const catalogItems = ref([]);
const catalogQuery = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const isEditorOpen = ref(false);
const form = reactive({
  id: null,
  currency: props.defaultCurrency,
  validUntil: '',
  notes: '',
  terms: '',
  items: [],
});

const hasCatalog = computed(() => hasFeature(FEATURE_FLAGS.BOTZ_CRM_CATALOG));
const statusLabels = computed(() => ({
  draft: t('COMMERCIAL.QUOTES.STATUS.DRAFT'),
  sent: t('COMMERCIAL.QUOTES.STATUS.SENT'),
  accepted: t('COMMERCIAL.QUOTES.STATUS.ACCEPTED'),
  rejected: t('COMMERCIAL.QUOTES.STATUS.REJECTED'),
  expired: t('COMMERCIAL.QUOTES.STATUS.EXPIRED'),
  cancelled: t('COMMERCIAL.QUOTES.STATUS.CANCELLED'),
}));
const statusClasses = {
  draft: 'bg-n-slate-3 text-n-slate-11',
  sent: 'bg-n-blue-3 text-n-blue-11',
  accepted: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
  expired: 'bg-n-amber-3 text-n-amber-11',
  cancelled: 'bg-n-slate-3 text-n-slate-9',
};
const editorTotals = computed(() =>
  form.items.reduce(
    (totals, item) => {
      const gross = Number(item.quantity || 0) * Number(item.unitPrice || 0);
      const discount = (gross * Number(item.discountRate || 0)) / 100;
      const subtotal = gross - discount;
      const tax = (subtotal * Number(item.taxRate || 0)) / 100;
      totals.subtotal += gross;
      totals.discount += discount;
      totals.tax += tax;
      totals.total += subtotal + tax;
      return totals;
    },
    { subtotal: 0, discount: 0, tax: 0, total: 0 }
  )
);
const date = value =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
        new Date(`${value}T00:00:00`)
      )
    : '—';
const money = (value, currency) =>
  `${currency || ''} ${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(value || 0))}`.trim();
const blankItem = () => ({
  commercialCatalogItemId: null,
  name: '',
  sku: '',
  description: '',
  quantity: 1,
  unit: '',
  unitPrice: 0,
  discountRate: 0,
  taxRate: 0,
  metadata: {},
});
const resetForm = () =>
  Object.assign(form, {
    id: null,
    currency: props.defaultCurrency,
    validUntil: '',
    notes: '',
    terms: '',
    items: [blankItem()],
  });
const loadQuotes = async () => {
  isLoading.value = true;
  try {
    const { data } = await CommercialQuotesAPI.list(props.dealId);
    quotes.value = camelcaseKeys(data.payload, { deep: true });
  } catch {
    useAlert(t('COMMERCIAL.QUOTES.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};
const quoteMeta = quote =>
  [
    date(quote.createdAt?.slice(0, 10)),
    `${t('COMMERCIAL.QUOTES.VALID_UNTIL')} ${date(quote.validUntil)}`,
  ].join(' · ');
const loadCatalog = async () => {
  if (!hasCatalog.value) return;
  try {
    const { data } = await CommercialCatalogAPI.list({
      q: catalogQuery.value,
      active: true,
      per_page: 100,
    });
    catalogItems.value = camelcaseKeys(data.payload, { deep: true });
  } catch {
    useAlert(t('COMMERCIAL.QUOTES.CATALOG.LOAD_ERROR'));
  }
};
const openNew = () => {
  resetForm();
  isEditorOpen.value = true;
  loadCatalog();
};
const openEdit = quote => {
  Object.assign(form, {
    id: quote.id,
    currency: quote.currency,
    validUntil: quote.validUntil || '',
    notes: quote.notes || '',
    terms: quote.terms || '',
    items: quote.items.map(item => ({ ...blankItem(), ...item })),
  });
  isEditorOpen.value = true;
  loadCatalog();
};
const closeEditor = () => {
  isEditorOpen.value = false;
  resetForm();
};
const addManualItem = () => form.items.push(blankItem());
const addCatalogItem = catalogItem => {
  const item = {
    ...blankItem(),
    commercialCatalogItemId: catalogItem.id,
    name: catalogItem.name,
    sku: catalogItem.sku || '',
    description: catalogItem.description || '',
    unit: catalogItem.unit || '',
    unitPrice: Number(catalogItem.basePrice || 0),
    taxRate: Number(catalogItem.taxRate || 0),
  };
  if (form.items.length === 1 && !form.items[0].name) form.items[0] = item;
  else form.items.push(item);
};
const removeItem = index => form.items.splice(index, 1);
const moveItem = (index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= form.items.length) return;
  [form.items[index], form.items[target]] = [
    form.items[target],
    form.items[index],
  ];
};
const saveQuote = async () => {
  isSaving.value = true;
  try {
    const payload = snakecaseKeys({ ...form }, { deep: true });
    const { data } = await CommercialQuotesAPI.save(props.dealId, payload);
    const saved = camelcaseKeys(data.payload, { deep: true });
    const index = quotes.value.findIndex(quote => quote.id === saved.id);
    if (index === -1) quotes.value.unshift(saved);
    else quotes.value[index] = saved;
    closeEditor();
    emit('changed');
    useAlert(t('COMMERCIAL.QUOTES.MESSAGES.SAVED'));
  } catch {
    useAlert(t('COMMERCIAL.QUOTES.MESSAGES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
const runAction = async (quote, action) => {
  try {
    const { data } =
      action === 'cancel'
        ? await CommercialQuotesAPI.remove(props.dealId, quote.id)
        : await CommercialQuotesAPI.action(props.dealId, quote.id, action);
    const updated = camelcaseKeys(data.payload, { deep: true });
    const index = quotes.value.findIndex(item => item.id === quote.id);
    if (action === 'duplicate') quotes.value.unshift(updated);
    else quotes.value[index] = updated;
    emit('changed');
  } catch {
    useAlert(t('COMMERCIAL.QUOTES.MESSAGES.ACTION_ERROR'));
  }
};

onMounted(() => {
  resetForm();
  loadQuotes();
});
</script>

<template>
  <section class="mx-auto max-w-5xl space-y-5">
    <header
      class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-gradient-to-br from-n-alpha-1 to-transparent p-5 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h2
          class="flex items-center gap-2 text-lg font-semibold text-n-slate-12"
        >
          <span class="i-lucide-receipt-text size-5 text-n-brand" />{{
            t('COMMERCIAL.QUOTES.TITLE')
          }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-9">
          {{ t('COMMERCIAL.QUOTES.DESCRIPTION') }}
        </p>
      </div>
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 rounded-xl bg-n-brand px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-n-brand/20"
        @click="openNew"
      >
        <span class="i-lucide-plus size-4" />{{ t('COMMERCIAL.QUOTES.NEW') }}
      </button>
    </header>

    <div v-if="isLoading" class="grid min-h-56 place-content-center">
      <span class="i-lucide-loader-circle size-7 animate-spin text-n-brand" />
    </div>
    <div
      v-else-if="!quotes.length"
      class="grid min-h-72 place-content-center rounded-2xl border border-dashed border-n-weak bg-n-alpha-1 p-8 text-center"
    >
      <span class="i-lucide-file-heart mx-auto size-12 text-n-brand" />
      <h3 class="mt-4 font-semibold text-n-slate-12">
        {{ t('COMMERCIAL.QUOTES.EMPTY.TITLE') }}
      </h3>
      <p class="mt-1 max-w-md text-sm text-n-slate-9">
        {{ t('COMMERCIAL.QUOTES.EMPTY.DESCRIPTION') }}
      </p>
      <button
        type="button"
        class="mx-auto mt-5 rounded-xl bg-n-brand px-4 py-2 text-sm font-medium text-white"
        @click="openNew"
      >
        {{ t('COMMERCIAL.QUOTES.NEW') }}
      </button>
    </div>
    <div v-else class="grid gap-4 lg:grid-cols-2">
      <article
        v-for="quote in quotes"
        :key="quote.id"
        class="group rounded-2xl border border-n-weak bg-n-background p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-n-brand/40 hover:shadow-lg"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <span class="font-mono text-sm font-semibold text-n-brand">{{
              quote.quoteNumber
            }}</span>
            <p class="mt-1 text-xs text-n-slate-9">{{ quoteMeta(quote) }}</p>
          </div>
          <span
            :class="statusClasses[quote.status]"
            class="rounded-full px-2.5 py-1 text-xs font-medium"
            >{{ statusLabels[quote.status] }}</span
          >
        </div>
        <div
          class="my-5 flex items-end justify-between border-y border-n-weak py-4"
        >
          <div>
            <p class="text-xs uppercase tracking-wide text-n-slate-8">
              {{ t('COMMERCIAL.QUOTES.TOTAL') }}
            </p>
            <strong class="mt-1 block text-2xl text-n-slate-12">{{
              money(quote.total, quote.currency)
            }}</strong>
          </div>
          <span class="text-sm text-n-slate-9">{{
            t('COMMERCIAL.QUOTES.ITEM_COUNT', { count: quote.items.length })
          }}</span>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
            @click="openEdit(quote)"
          >
            {{ t('COMMERCIAL.QUOTES.ACTIONS.VIEW_EDIT') }}</button
          ><button
            type="button"
            class="rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
            @click="runAction(quote, 'duplicate')"
          >
            {{ t('COMMERCIAL.QUOTES.ACTIONS.DUPLICATE') }}</button
          ><button
            v-if="quote.status === 'draft'"
            type="button"
            class="rounded-lg border border-n-blue-5 px-3 py-1.5 text-xs text-n-blue-11"
            @click="runAction(quote, 'mark_sent')"
          >
            {{ t('COMMERCIAL.QUOTES.ACTIONS.MARK_SENT') }}</button
          ><button
            v-if="quote.status === 'sent'"
            type="button"
            class="rounded-lg border border-n-teal-5 px-3 py-1.5 text-xs text-n-teal-11"
            @click="runAction(quote, 'accept')"
          >
            {{ t('COMMERCIAL.QUOTES.ACTIONS.ACCEPT') }}</button
          ><button
            v-if="quote.status === 'sent'"
            type="button"
            class="rounded-lg border border-n-ruby-5 px-3 py-1.5 text-xs text-n-ruby-11"
            @click="runAction(quote, 'reject')"
          >
            {{ t('COMMERCIAL.QUOTES.ACTIONS.REJECT') }}</button
          ><button
            v-if="!['accepted', 'cancelled'].includes(quote.status)"
            type="button"
            class="rounded-lg px-3 py-1.5 text-xs text-n-slate-9 hover:bg-n-alpha-2"
            @click="runAction(quote, 'cancel')"
          >
            {{ t('COMMERCIAL.QUOTES.ACTIONS.CANCEL') }}
          </button>
        </div>
      </article>
    </div>

    <Teleport to="body">
      <div
        v-if="isEditorOpen"
        class="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4"
        @click.self="closeEditor"
      >
        <form
          class="flex max-h-[100dvh] w-full max-w-5xl flex-col overflow-hidden border border-n-weak bg-n-background shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl"
          @submit.prevent="saveQuote"
        >
          <header
            class="flex items-center justify-between border-b border-n-weak p-4 sm:p-6"
          >
            <div>
              <h3 class="text-lg font-semibold text-n-slate-12">
                {{
                  form.id
                    ? t('COMMERCIAL.QUOTES.EDITOR.EDIT')
                    : t('COMMERCIAL.QUOTES.EDITOR.NEW')
                }}
              </h3>
              <p class="text-sm text-n-slate-9">
                {{ t('COMMERCIAL.QUOTES.EDITOR.DESCRIPTION') }}
              </p>
            </div>
            <button
              type="button"
              class="i-lucide-x size-5 text-n-slate-9"
              :aria-label="t('COMMERCIAL.QUOTES.ACTIONS.CLOSE')"
              @click="closeEditor"
            />
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <div class="grid gap-5 xl:grid-cols-[1fr_300px]">
              <main class="space-y-5">
                <section
                  class="grid gap-4 rounded-2xl border border-n-weak p-4 sm:grid-cols-2"
                >
                  <label class="flex flex-col gap-1.5 text-sm text-n-slate-10"
                    >{{ t('COMMERCIAL.QUOTES.FIELDS.CURRENCY')
                    }}<input
                      v-model.trim="form.currency"
                      required
                      maxlength="3"
                      class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 uppercase text-n-slate-12" /></label
                  ><label class="flex flex-col gap-1.5 text-sm text-n-slate-10"
                    >{{ t('COMMERCIAL.QUOTES.FIELDS.VALID_UNTIL')
                    }}<input
                      v-model="form.validUntil"
                      type="date"
                      class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                  /></label>
                </section>
                <section
                  v-if="hasCatalog"
                  class="rounded-2xl border border-n-weak bg-n-alpha-1 p-4"
                >
                  <div class="flex flex-col gap-3 sm:flex-row">
                    <input
                      v-model.trim="catalogQuery"
                      :placeholder="t('COMMERCIAL.QUOTES.CATALOG.SEARCH')"
                      class="h-10 flex-1 rounded-lg border border-n-weak bg-n-background px-3 text-sm"
                      @keyup.enter.prevent="loadCatalog"
                    /><button
                      type="button"
                      class="rounded-lg border border-n-weak px-4 py-2 text-sm"
                      @click="loadCatalog"
                    >
                      {{ t('COMMERCIAL.QUOTES.CATALOG.SEARCH_ACTION') }}
                    </button>
                  </div>
                  <div
                    v-if="catalogItems.length"
                    class="mt-3 grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2"
                  >
                    <button
                      v-for="item in catalogItems"
                      :key="item.id"
                      type="button"
                      class="flex items-center justify-between rounded-xl border border-n-weak bg-n-background p-3 text-left hover:border-n-brand"
                      @click="addCatalogItem(item)"
                    >
                      <span
                        ><strong class="block text-sm text-n-slate-12">{{
                          item.name
                        }}</strong
                        ><small class="text-n-slate-9">{{
                          item.sku || item.category || '—'
                        }}</small></span
                      ><span class="text-xs font-medium text-n-brand">{{
                        money(item.basePrice, item.currency || form.currency)
                      }}</span>
                    </button>
                  </div>
                  <p v-else class="mt-3 text-sm text-n-slate-9">
                    {{ t('COMMERCIAL.QUOTES.CATALOG.EMPTY') }}
                  </p>
                </section>
                <div
                  v-else
                  class="rounded-xl border border-n-amber-5 bg-n-amber-2 p-4 text-sm text-n-amber-11"
                >
                  {{ t('COMMERCIAL.QUOTES.CATALOG.DISABLED') }}
                </div>
                <section class="space-y-3">
                  <div class="flex items-center justify-between">
                    <h4 class="font-semibold text-n-slate-12">
                      {{ t('COMMERCIAL.QUOTES.ITEMS.TITLE') }}
                    </h4>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-11"
                      @click="addManualItem"
                    >
                      <span class="i-lucide-plus size-4" />{{
                        t('COMMERCIAL.QUOTES.ITEMS.ADD_MANUAL')
                      }}
                    </button>
                  </div>
                  <article
                    v-for="(item, index) in form.items"
                    :key="index"
                    class="rounded-2xl border border-n-weak bg-n-background p-4"
                  >
                    <div class="mb-3 flex items-center justify-between">
                      <span
                        class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-9"
                        >{{ index + 1 }}</span
                      >
                      <div class="flex gap-1">
                        <button
                          type="button"
                          class="i-lucide-arrow-up size-4 text-n-slate-9"
                          :disabled="index === 0"
                          @click="moveItem(index, -1)"
                        /><button
                          type="button"
                          class="i-lucide-arrow-down size-4 text-n-slate-9"
                          :disabled="index === form.items.length - 1"
                          @click="moveItem(index, 1)"
                        /><button
                          type="button"
                          class="i-lucide-trash-2 ml-2 size-4 text-n-ruby-9"
                          @click="removeItem(index)"
                        />
                      </div>
                    </div>
                    <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <label class="sm:col-span-2 lg:col-span-3"
                        ><span class="mb-1 block text-xs text-n-slate-9">{{
                          t('COMMERCIAL.QUOTES.ITEMS.NAME')
                        }}</span
                        ><input
                          v-model.trim="item.name"
                          required
                          :readonly="Boolean(item.commercialCatalogItemId)"
                          class="h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label
                      ><label
                        ><span class="mb-1 block text-xs text-n-slate-9">{{
                          t('COMMERCIAL.QUOTES.ITEMS.SKU')
                        }}</span
                        ><input
                          v-model.trim="item.sku"
                          :readonly="Boolean(item.commercialCatalogItemId)"
                          class="h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label
                      ><label
                        ><span class="mb-1 block text-xs text-n-slate-9">{{
                          t('COMMERCIAL.QUOTES.ITEMS.QUANTITY')
                        }}</span
                        ><input
                          v-model.number="item.quantity"
                          required
                          type="number"
                          min="0.0001"
                          step="0.01"
                          class="h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label
                      ><label
                        ><span class="mb-1 block text-xs text-n-slate-9">{{
                          t('COMMERCIAL.QUOTES.ITEMS.UNIT_PRICE')
                        }}</span
                        ><input
                          v-model.number="item.unitPrice"
                          required
                          type="number"
                          min="0"
                          step="0.01"
                          :readonly="Boolean(item.commercialCatalogItemId)"
                          class="h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label
                      ><label
                        ><span class="mb-1 block text-xs text-n-slate-9">{{
                          t('COMMERCIAL.QUOTES.ITEMS.DISCOUNT')
                        }}</span
                        ><input
                          v-model.number="item.discountRate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          class="h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label
                      ><label
                        ><span class="mb-1 block text-xs text-n-slate-9">{{
                          t('COMMERCIAL.QUOTES.ITEMS.TAX')
                        }}</span
                        ><input
                          v-model.number="item.taxRate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          :readonly="Boolean(item.commercialCatalogItemId)"
                          class="h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
                      /></label>
                    </div>
                  </article>
                </section>
                <section
                  class="grid gap-4 rounded-2xl border border-n-weak p-4 sm:grid-cols-2"
                >
                  <label class="flex flex-col gap-1.5 text-sm text-n-slate-10"
                    >{{ t('COMMERCIAL.QUOTES.FIELDS.NOTES')
                    }}<textarea
                      v-model.trim="form.notes"
                      rows="4"
                      class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-n-slate-12"
                    /></label
                  ><label class="flex flex-col gap-1.5 text-sm text-n-slate-10"
                    >{{ t('COMMERCIAL.QUOTES.FIELDS.TERMS')
                    }}<textarea
                      v-model.trim="form.terms"
                      rows="4"
                      class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-n-slate-12"
                    />
                  </label>
                </section>
              </main>
              <aside>
                <div
                  class="sticky top-0 rounded-2xl border border-n-brand/30 bg-gradient-to-br from-n-brand/10 to-transparent p-5"
                >
                  <h4 class="font-semibold text-n-slate-12">
                    {{ t('COMMERCIAL.QUOTES.SUMMARY.TITLE') }}
                  </h4>
                  <dl class="mt-4 space-y-3 text-sm">
                    <div class="flex justify-between">
                      <dt class="text-n-slate-9">
                        {{ t('COMMERCIAL.QUOTES.SUMMARY.SUBTOTAL') }}
                      </dt>
                      <dd>{{ money(editorTotals.subtotal, form.currency) }}</dd>
                    </div>
                    <div class="flex justify-between">
                      <dt class="text-n-slate-9">
                        {{ t('COMMERCIAL.QUOTES.SUMMARY.DISCOUNT') }}
                      </dt>
                      <dd>
                        {{ money(-editorTotals.discount, form.currency) }}
                      </dd>
                    </div>
                    <div class="flex justify-between">
                      <dt class="text-n-slate-9">
                        {{ t('COMMERCIAL.QUOTES.SUMMARY.TAX') }}
                      </dt>
                      <dd>{{ money(editorTotals.tax, form.currency) }}</dd>
                    </div>
                    <div
                      class="flex justify-between border-t border-n-weak pt-4 text-lg font-semibold"
                    >
                      <dt>{{ t('COMMERCIAL.QUOTES.SUMMARY.TOTAL') }}</dt>
                      <dd class="text-n-brand">
                        {{ money(editorTotals.total, form.currency) }}
                      </dd>
                    </div>
                  </dl>
                  <button
                    type="button"
                    disabled
                    class="mt-5 w-full rounded-xl border border-n-weak px-4 py-2 text-sm text-n-slate-8 opacity-60"
                  >
                    <span
                      class="i-lucide-file-text mr-2 inline-block size-4"
                    />{{ t('COMMERCIAL.QUOTES.PDF_SOON') }}
                  </button>
                </div>
              </aside>
            </div>
          </div>
          <footer
            class="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-n-weak bg-n-background p-4 sm:p-6"
          >
            <button
              type="button"
              class="rounded-xl border border-n-weak px-4 py-2 text-sm text-n-slate-11"
              @click="closeEditor"
            >
              {{ t('COMMERCIAL.QUOTES.ACTIONS.CLOSE') }}</button
            ><button
              type="submit"
              :disabled="isSaving || !form.items.length"
              class="rounded-xl bg-n-brand px-5 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {{ t('COMMERCIAL.QUOTES.ACTIONS.SAVE_DRAFT') }}
            </button>
          </footer>
        </form>
      </div>
    </Teleport>
  </section>
</template>
